import { isFuture } from 'date-fns';
import { useMemo } from 'react';

import {
  Currency,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { Card } from '@sorare/core/src/lib/cards';

export const useSortByPrice = <T extends Card>() => {
  const { toMonetaryAmount, toMonetaryAmountInReferenceCurrency } =
    useMonetaryAmount();
  const { fiatCurrency: userFiatCurrency, currency: userCurrency } =
    useCurrentUserContext();

  return useMemo(() => {
    const sortCurrency =
      userCurrency === Currency.ETH
        ? SupportedCurrency.WEI
        : userFiatCurrency.code;

    const getPriceRangeAverage = (
      priceRange: NonNullable<Card['priceRange']>
    ) => {
      const monetaryAmount = toMonetaryAmountInReferenceCurrency(
        (priceRange.max + priceRange.min) / 2n,
        SupportedCurrency.WEI
      );
      return monetaryAmount.readAmount(sortCurrency);
    };

    const getCardPrice = (card: T): bigint | number => {
      const {
        liveSingleSaleOffer,
        tokenOwner,
        latestPrimaryOffer,
        latestEnglishAuction,
        priceRange,
      } = card;

      if (liveSingleSaleOffer) {
        const monetaryAmount = toMonetaryAmount(
          liveSingleSaleOffer.receiverSide.amounts
        );
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (tokenOwner?.amounts?.wei && tokenOwner.amounts.wei !== 0n) {
        const monetaryAmount = toMonetaryAmount(tokenOwner.amounts);
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (latestPrimaryOffer?.price && isFuture(latestPrimaryOffer.endDate)) {
        const monetaryAmount = toMonetaryAmount(latestPrimaryOffer.price);
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (latestEnglishAuction && isFuture(latestEnglishAuction.endDate)) {
        const { currency, currentPrice } = latestEnglishAuction;
        const monetaryAmount = toMonetaryAmount({
          [currency.toLowerCase()]: currentPrice,
          referenceCurrency: currency,
        });
        return monetaryAmount.readAmount(sortCurrency);
      }
      if (priceRange) {
        return getPriceRangeAverage(priceRange);
      }
      return 0n;
    };

    return (card: T[], sortOrder: 'asc' | 'desc') => {
      const res = [...card];
      res.sort((a, b) => Number(getCardPrice(b)) - Number(getCardPrice(a)));
      if (sortOrder === 'asc') res.reverse();
      return res;
    };
  }, [
    toMonetaryAmount,
    toMonetaryAmountInReferenceCurrency,
    userCurrency,
    userFiatCurrency.code,
  ]);
};
